import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../tokens";
import { Btn } from "../components/Btn";
import { AuthShell } from "../components/AuthShell";
import { TargetRoleEditor, WORK_MODE_TAG_VALUES } from "../components/TargetRoleEditor";
import { useLogout } from "../hooks/useAuth";
import { useActiveResume } from "../hooks/useResume";
import { useGenerateMatches, useEnsureFirstMatchGeneration } from "../hooks/useMatches";
import { useLaunchConfig } from "../lib/launch-config";
import { track } from "../lib/analytics";

const ACCEPTED_EXTENSIONS = ".pdf,.docx";
const MAX_FILE_MB = 10;
/** v1.1 first run (FIRST_RUN_V11_ENABLED): longest wait for the first matches before opening /matches anyway. */
const FIRST_MATCHES_WAIT_MS = 60_000;

// A single synchronous POST /resumes request does the file read, Claude
// parse, embedding and DB write in one round trip — there is no separate
// status endpoint to poll (see packages/api/src/routes/resumes.ts). These
// "stages" are cosmetic label changes over the lifetime of that one request,
// not a real multi-step workflow — no extra requests are made for them.
interface UploadedResume {
  targetRoles?:      string[] | null;
  targetLocations?:  string[] | null;
  workAuthTags?:      string[] | null;
  seniorityLevel?:   string | null;
  clarity?:          { ambiguous: boolean } | null;
}

type Phase =
  | { kind: "idle" }
  | { kind: "uploading"; stage: "uploading" | "parsing" | "preparing" }
  // Role analysis was ambiguous — Phase 5's "select primary role before
  // continuing to matches" step. Carries the just-uploaded resume so the
  // role editor can be prefilled without a second fetch.
  | { kind: "confirming_role"; resume: UploadedResume }
  | { kind: "generating" }
  | { kind: "first_matches" }
  | { kind: "completed" }
  | { kind: "failed"; message: string }
  | { kind: "rate_limited"; message: string }
  | { kind: "disabled"; message: string };

function statusOf(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | undefined)?.response?.status;
}

function bodyMessageOf(err: unknown): string | undefined {
  const data = (err as { response?: { data?: { message?: string; error?: string } } } | undefined)?.response?.data;
  // The kill-switch/budget/global-handler responses carry `.message` (a safe,
  // pre-written string); the two hand-written 400/422 responses in
  // resumes.ts only carry `.error` — check both.
  return data?.message ?? data?.error;
}

function retryAfterSecondsOf(err: unknown): number | undefined {
  const header = (err as { response?: { headers?: Record<string, string> } } | undefined)?.response?.headers?.["retry-after"];
  const n = header ? Number(header) : NaN;
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function humanizeRetryAfter(seconds: number): string {
  if (seconds < 3600) return `about ${Math.max(1, Math.round(seconds / 60))} minute${seconds < 90 ? "" : "s"}`;
  if (seconds < 86400) return `about ${Math.round(seconds / 3600)} hour${seconds < 5400 ? "" : "s"}`;
  return "about a day";
}

const EXPLAINER_POINTS = [
  "Calibre uses the resume to understand experience, skills and target roles.",
  "Accepted formats are PDF and DOCX.",
  "Maximum size is 10 MB.",
  "Calibre is currently in Beta.",
  "Recommendations may be incomplete.",
  "Calibre does not automatically apply for jobs.",
  "Resume processing may use approved AI providers.",
];

export function ResumeOnboarding() {
  const navigate = useNavigate();
  const { flags } = useLaunchConfig();   // v1.1 FIRST_RUN_V11_ENABLED (off → r2b flow)
  const qc = useQueryClient();
  const logout = useLogout();
  const fileRef = useRef<HTMLInputElement>(null);
  const stageTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });

  const busy = phase.kind === "uploading";
  const generateMatches = useGenerateMatches();
  const ensureFirstMatches = useEnsureFirstMatchGeneration();   // v1.1 first run only

  // Safeguard for direct/back navigation to this URL while a resume is
  // already active (this page is not wrapped by ResumeGate — it's the
  // destination the gate redirects TO, not one of the pages it protects).
  // Skipped while an upload is in flight or has just completed so this
  // never races the post-upload navigate("/") below.
  const activeResumeQuery = useActiveResume();
  useEffect(() => {
    if (phase.kind === "idle" && activeResumeQuery.isSuccess) {
      navigate("/", { replace: true });
    }
  }, [phase.kind, activeResumeQuery.isSuccess, navigate]);

  const clearStageTimers = useCallback(() => {
    stageTimers.current.forEach(clearTimeout);
    stageTimers.current = [];
  }, []);

  // Never leave a pending timer running past unmount (e.g. a fast redirect).
  useEffect(() => () => clearStageTimers(), [clearStageTimers]);

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input immediately so re-selecting the same file after a
    // failed attempt fires onChange again (browsers dedupe identical values).
    e.target.value = "";
    if (!file || busy) return;

    setPhase({ kind: "uploading", stage: "uploading" });
    clearStageTimers();
    stageTimers.current.push(
      setTimeout(() => setPhase((p) => (p.kind === "uploading" ? { kind: "uploading", stage: "parsing" } : p)), 1200),
      setTimeout(() => setPhase((p) => (p.kind === "uploading" ? { kind: "uploading", stage: "preparing" } : p)), 3500),
    );

    try {
      const fd = new FormData();
      fd.append("resume", file);
      const { data } = await axios.post<UploadedResume>("/api/resumes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      clearStageTimers();
      await qc.invalidateQueries({ queryKey: ["resume", "active"] });

      // Phase 5: ambiguous → require a primary-role choice before matching;
      // clear → proceed straight to the dashboard as before (matching already
      // ran automatically on upload — see auto-semantic-match.ts).
      if (data.clarity?.ambiguous) {
        setPhase({ kind: "confirming_role", resume: data });
        return;
      }
      track("resume_uploaded");
      if (flags.firstRun) {
        // v1.1 first run: wait (bounded) for the first matches, then open them.
        setPhase({ kind: "first_matches" });
        // Bounded wait (60 s); any failure or timeout still opens /matches, which explains.
        await Promise.race([
          ensureFirstMatches.mutateAsync().catch(() => undefined),
          new Promise((resolve) => setTimeout(resolve, FIRST_MATCHES_WAIT_MS)),
        ]);
        setPhase({ kind: "completed" });
        navigate("/matches", { replace: true });
        return;
      }
      setPhase({ kind: "completed" });
      navigate("/", { replace: true });
    } catch (err: unknown) {
      clearStageTimers();
      const status = statusOf(err);
      if (flags.firstRun && status === 503 && bodyMessageOf(err)) {
        // v1.1: the daily-capacity / busy message from the server says when to come back.
        setPhase({ kind: "disabled", message: bodyMessageOf(err)! });
      } else if (status === 403 || status === 503) {
        // Exact required copy for the upload kill switch — shown regardless
        // of the backend's own message text, per spec.
        setPhase({ kind: "disabled", message: "Resume uploads are temporarily unavailable. Please try again later." });
      } else if (status === 429) {
        const retryAfter = retryAfterSecondsOf(err);
        const base = bodyMessageOf(err) ?? "You've reached the resume upload limit. Please try again later.";
        setPhase({ kind: "rate_limited", message: retryAfter ? `${base} Try again in ${humanizeRetryAfter(retryAfter)}.` : base });
      } else if (status === 413) {
        setPhase({ kind: "failed", message: `Your resume is larger than ${MAX_FILE_MB} MB. Please upload a smaller PDF or DOCX.` });
      } else if (status === 400 || status === 422) {
        setPhase({ kind: "failed", message: bodyMessageOf(err) ?? "We couldn't process that file. Please try a different PDF or DOCX." });
      } else {
        // 5xx, network failure, or anything unrecognized — never echo internals.
        setPhase({ kind: "failed", message: "Something went wrong. Please try again in a moment." });
      }
    }
  }, [busy, clearStageTimers, navigate, qc, flags.firstRun]);

  // After the role is confirmed (TargetRoleEditor already saved it via PATCH
  // /resumes/preferences), explicitly run the $0 candidate-safe generator —
  // per Phase 6, do not imply the role edit itself generated matches; run the
  // action and show whatever it honestly finds on the next screen. No re-parse
  // or re-upload of the resume happens here — only the existing preferences
  // row was updated, then this reuses the same generator upload already runs.
  const handleRoleConfirmed = useCallback(() => {
    setPhase({ kind: "generating" });
    generateMatches.mutate(undefined, {
      onSettled: () => navigate("/", { replace: true }),
    });
  }, [generateMatches, navigate]);

  const stageLabel = phase.kind === "uploading"
    ? { uploading: "Uploading your resume…", parsing: "Parsing resume…", preparing: "Preparing your profile…" }[phase.stage]
    : null;

  const cardStyle: React.CSSProperties = {
    width: "100%", padding: "32px", background: T.s1,
    border: `1px solid ${T.b0}`, borderRadius: "12px",
  };

  // v1.1 FIRST_RUN_V11_ENABLED: a clear "first matches" step instead of a silent redirect.
  if (phase.kind === "first_matches") {
    return (
      <AuthShell>
        <div style={cardStyle} role="status" aria-live="polite" data-testid="first-matches">
          <h1 style={{ fontFamily: T.display, fontSize: "20px", fontWeight: 700, color: T.t0, margin: "0 0 8px" }}>Résumé saved — finding your first matches</h1>
          <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0, lineHeight: 1.6 }}>
            We're comparing your résumé with the jobs we have. This usually takes under a minute. Calibre works best for tech roles in India.
          </p>
        </div>
      </AuthShell>
    );
  }

  // ── Role confirmation (ambiguous target role) / generating matches ────────
  // A distinct screen, not a modal over the upload form — Phase 5's guided
  // "select primary role → save → run matching → show results" sequence.
  if (phase.kind === "confirming_role" || phase.kind === "generating") {
    const uploaded = phase.kind === "confirming_role" ? phase.resume : null;
    return (
      <AuthShell>
        <div style={cardStyle}>
          <h1 style={{ fontFamily: T.display, fontSize: "20px", fontWeight: 700, color: T.t0, margin: "0 0 8px", lineHeight: 1.3 }}>
            We found several possible directions
          </h1>
          <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: "0 0 22px", lineHeight: 1.5 }}>
            Your experience fits several directions. Choose the role you want Calibre to prioritise.
          </p>
          {phase.kind === "generating" ? (
            <div role="status" aria-live="polite" style={{ textAlign: "center", padding: "12px 0" }}>
              <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2 }}>Finding your matches…</p>
            </div>
          ) : (
            <TargetRoleEditor
              saveLabel="Save and continue"
              initial={{
                primaryRole:       uploaded?.targetRoles?.[0] ?? "",
                secondaryRoles:    (uploaded?.targetRoles ?? []).slice(1),
                locations:         uploaded?.targetLocations ?? [],
                workMode:          (uploaded?.workAuthTags ?? []).find((t) => WORK_MODE_TAG_VALUES.includes(t)) ?? "",
                seniorityLevel:    uploaded?.seniorityLevel ?? "",
                otherWorkAuthTags: (uploaded?.workAuthTags ?? []).filter((t) => !WORK_MODE_TAG_VALUES.includes(t)),
              }}
              onSaved={handleRoleConfirmed}
            />
          )}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div style={cardStyle}>
        <h1 style={{ fontFamily: T.display, fontSize: "22px", fontWeight: 700, color: T.t0, margin: "0 0 12px", lineHeight: 1.25 }}>
          Upload your resume to find matching jobs
        </h1>

        <ul style={{ margin: "0 0 22px", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "7px" }}>
          {EXPLAINER_POINTS.map((point) => (
            <li key={point} style={{
              display: "flex", gap: "8px", alignItems: "flex-start",
              fontFamily: T.body, fontSize: "12.5px", color: T.t2, lineHeight: 1.5,
            }}>
              <span aria-hidden="true" style={{ color: T.tealSoft, marginTop: "1px" }}>•</span>
              {point}
            </li>
          ))}
        </ul>

        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          aria-label="Upload resume — PDF or DOCX, maximum 10 MB"
          style={{ display: "none" }}
          onChange={handleFile}
          disabled={busy}
        />

        <Btn
          variant="cta" size="lg"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          style={{ width: "100%", justifyContent: "center", marginBottom: "10px" }}
        >
          {busy ? (stageLabel ?? "Uploading…") : "Upload Resume"}
        </Btn>

        {/* Status region — one live area for every non-idle phase, so a
            screen reader announces state changes without needing to be
            re-focused, and no separate modal ever reopens itself. */}
        <div role="status" aria-live="polite" style={{ minHeight: "18px" }}>
          {phase.kind === "failed" && (
            <p role="alert" style={{ fontFamily: T.body, fontSize: "12px", color: T.err, margin: "6px 0 0" }}>
              {phase.message}
            </p>
          )}
          {phase.kind === "rate_limited" && (
            <p role="alert" style={{ fontFamily: T.body, fontSize: "12px", color: T.warn, margin: "6px 0 0" }}>
              {phase.message}
            </p>
          )}
          {phase.kind === "disabled" && (
            <p role="alert" style={{ fontFamily: T.body, fontSize: "12px", color: T.warn, margin: "6px 0 0" }}>
              {phase.message}
            </p>
          )}
          {phase.kind === "completed" && (
            <p style={{ fontFamily: T.body, fontSize: "12px", color: T.ok, margin: "6px 0 0" }}>
              Resume uploaded. Taking you to your matches…
            </p>
          )}
        </div>

        <div style={{ height: "1px", background: T.b0, margin: "20px 0 16px" }} />

        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
            <button
              type="button" onClick={() => navigate("/profile")}
              style={{ background: "none", border: "none", color: T.tealSoft, cursor: "pointer",
                fontFamily: T.body, fontSize: "12px", padding: 0, textDecoration: "underline", textUnderlineOffset: "2px" }}
            >
              Account settings
            </button>
            <button
              type="button" onClick={() => logout.mutate()}
              style={{ background: "none", border: "none", color: T.tealSoft, cursor: "pointer",
                fontFamily: T.body, fontSize: "12px", padding: 0, textDecoration: "underline", textUnderlineOffset: "2px" }}
            >
              Sign out
            </button>
          </div>
          <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, margin: 0, lineHeight: 1.5 }}>
            {flags.honestCopy
              ? (flags.dataExport ? "Once you're in, Profile lets you download your data or delete your account at any time." : "Once you're in, you can delete your account from Profile at any time.")
              : "Contact support to request account or resume deletion, or to ask a question."}
          </p>
        </div>
      </div>
    </AuthShell>
  );
}
